import { NATIVE_OR_SIM } from '../proximity/native/simPhone';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { focusManager, onlineManager } from '@tanstack/react-query';
import { runRealtimeResumeHandlers } from '../realtime/resumeRegistry';
import { initConnectivityRealtimeGate } from '../realtime/connectivityRealtimeGate';
import {
  initConnectivityRecoveryProbe,
  runConnectivityProbeNow,
} from '../network/connectivityRecoveryProbe';
import {
  setNetworkAssociationHint,
  shouldPauseQueriesForConnectivity,
  subscribeEffectiveConnectivityInputs,
} from '../network/effectiveConnectivity';
import { flushPersistedQueryCacheSync } from './persistFlushRegistry';
import { persistFindRemoteLogsNow, flushFindRemoteLogs } from '../findRemoteLog';
import { queryKeys } from './queryKeys';
import { requestFindPeersRefresh } from '../proximity/data/findPeersRefresh';
import { getSupabase } from '../../supabaseClient';

/**
 * supabase-js refreshes the access token on a setInterval, which iOS suspends in the
 * background and never re-arms. At Red Rocks a session that started at 17:58 began
 * failing writes with 42501 at 19:03 — about one JWT lifetime later — and Find went
 * silently dead. Tie the refresh ticker to app lifecycle and force a session check on
 * resume so the token is valid *before* the first write, not after a 403.
 */
function stopAuthAutoRefresh() {
  try {
    void getSupabase().auth.stopAutoRefresh();
  } catch (err) {
    console.warn('[auth] stopAutoRefresh failed:', err);
  }
}

function resumeAuthAutoRefresh() {
  try {
    const auth = getSupabase().auth;
    void auth.startAutoRefresh();
    // getSession() refreshes when the token is expired or close to it.
    void auth.getSession().catch((err) => console.warn('[auth] getSession failed:', err));
  } catch (err) {
    console.warn('[auth] startAutoRefresh failed:', err);
  }
}

/** Invalidate chat/group queries after background — catches messages missed offline. */
function invalidateChatQueriesOnResume(queryClient, userId) {
  if (!queryClient) return;
  queryClient.invalidateQueries({ queryKey: ['chats'] });
  if (userId) {
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.inbox(userId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.chats.pending(userId) });
    // The bell: notifications are written the instant their cause happens (2026-10-12 check), but the realtime channel
    // that tells the app rarely survives a pocket - so a resume asks, rather than waiting for the channel to rejoin.
    queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(userId) });
    queryClient.invalidateQueries({ queryKey: ['notifications', 'list', userId] });
    // Maps' your-events list: an RSVP made on the web, or replayed after a restart, would otherwise wait 20 min.
    queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.mine(userId) });
  }
}

/**
 * A membership or follow-back notification that landed while the app was in a pocket: the realtime row that would have
 * refreshed Find was never delivered, and nothing re-reads it later (2026-10-16 audit). Runs even when connectivity
 * paused queries - an invalidated query refetches the moment queries resume, and Find's peer re-read keeps its last
 * good list if it fails - so a resume in a dead zone still recovers once signal returns.
 */
function refreshFindOnResume(queryClient) {
  if (!queryClient) return;
  // An approval that landed while the app was in a pocket: Find and Maps read ['find','groups'] (2026-10-03).
  queryClient.invalidateQueries({ queryKey: ['find'] });
  queryClient.invalidateQueries({ queryKey: ['groups'] });
  requestFindPeersRefresh('resume');
}

/**
 * Service came back (reachability, not just the radio - this fires from the fetch/realtime probe too): refresh an
 * access token that expired while offline right away instead of on the next 30 s auto-refresh tick. The app kept you
 * signed in on the stored session meanwhile (lib/auth/storedSession); this is what turns it back into a live one.
 */
function refreshAuthAfterReconnect() {
  try {
    void getSupabase()
      .auth.getSession()
      .catch((err) => console.warn('[auth] getSession after reconnect failed:', err));
  } catch (err) {
    console.warn('[auth] getSession after reconnect failed:', err);
  }
}

let lastQueriesOnline = null;

/** Only unreachable pauses React Query — degraded keeps queries running. */
function syncOnlineManagerFromEffectiveConnectivity() {
  const online = !shouldPauseQueriesForConnectivity();
  onlineManager.setOnline(online);
  if (online && lastQueriesOnline === false) refreshAuthAfterReconnect();
  lastQueriesOnline = online;
}

/**
 * Wire TanStack Query focus/online managers to Capacitor lifecycle.
 * Browser falls back to window focus / online events.
 */
export function initCapacitorQueryLifecycle(queryClient, getUserId, { onResumeMutations } = {}) {
  let appListener;
  let networkListener;
  let connectivityUnsubscribe;
  let windowFocusHandler;
  let windowOnlineHandler;
  let windowOfflineHandler;

  initConnectivityRecoveryProbe();
  initConnectivityRealtimeGate();

  connectivityUnsubscribe = subscribeEffectiveConnectivityInputs(() => {
    syncOnlineManagerFromEffectiveConnectivity();
  });
  syncOnlineManagerFromEffectiveConnectivity();

  const onResume = () => {
    focusManager.setFocused(true);
    // Before anything else tries to write — Find's publisher restarts on this same event.
    resumeAuthAutoRefresh();
    void runConnectivityProbeNow();
    refreshFindOnResume(queryClient); // before the early return: it must run offline too (2026-10-16 audit)
    if (shouldPauseQueriesForConnectivity()) return;
    const userId = typeof getUserId === 'function' ? getUserId() : null;
    invalidateChatQueriesOnResume(queryClient, userId);
    runRealtimeResumeHandlers();
    onResumeMutations?.();
  };

  const onPause = () => {
    focusManager.setFocused(false);
    stopAuthAutoRefresh();
    flushPersistedQueryCacheSync('app-pause');
    void persistFindRemoteLogsNow();
    void flushFindRemoteLogs('app-pause');
  };

  let windowPageHideHandler;

  // A sim phone (scripts/sim) takes the app-state path too: its pocket is what the runner tests.
  if (NATIVE_OR_SIM) {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) onResume();
      else onPause();
    }).then((handle) => {
      appListener = handle;
    });

    Network.getStatus()
      .then((status) => {
        setNetworkAssociationHint(status.connected);
      })
      .catch((err) => console.warn('[query] Network.getStatus failed:', err));

    Network.addListener('networkStatusChange', (status) => {
      setNetworkAssociationHint(status.connected);
      if (status.connected) {
        void runConnectivityProbeNow();
        // Hidden: a Wi-Fi flap in a pocket ran the whole resume (focus on, every list invalidated and refetched,
        // realtime rebuilt) four times in two minutes on 2026-10-29. The real resume does all of it on the way back.
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        onResume();
      }
    }).then((handle) => {
      networkListener = handle;
    });
  } else if (typeof window !== 'undefined') {
    windowPageHideHandler = () => {
      flushPersistedQueryCacheSync('pagehide');
    };
    windowFocusHandler = () => {
      const visible = document.visibilityState !== 'hidden';
      focusManager.setFocused(visible);
      if (visible) onResume();
      else flushPersistedQueryCacheSync('visibility-hidden');
    };
    windowOnlineHandler = () => {
      setNetworkAssociationHint(true);
      void runConnectivityProbeNow();
      onResume();
    };
    windowOfflineHandler = () => setNetworkAssociationHint(false);

    document.addEventListener('visibilitychange', windowFocusHandler);
    window.addEventListener('pagehide', windowPageHideHandler);
    window.addEventListener('online', windowOnlineHandler);
    window.addEventListener('offline', windowOfflineHandler);
    focusManager.setFocused(document.visibilityState !== 'hidden');
    setNetworkAssociationHint(navigator.onLine);
    syncOnlineManagerFromEffectiveConnectivity();
  }

  return () => {
    connectivityUnsubscribe?.();
    appListener?.remove();
    networkListener?.remove();
    if (windowFocusHandler) document.removeEventListener('visibilitychange', windowFocusHandler);
    if (windowPageHideHandler) window.removeEventListener('pagehide', windowPageHideHandler);
    if (windowOnlineHandler) window.removeEventListener('online', windowOnlineHandler);
    if (windowOfflineHandler) window.removeEventListener('offline', windowOfflineHandler);
  };
}
